/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Plus } from 'lucide-react';

/**
 * Editable review of the AI-extracted structured note. Two field types:
 *   - text : Textarea
 *   - list : chip input (Enter to add, X to remove)
 */
export const StructuredReview = ({ schema = [], value = {}, onChange, disabled = false }) => {
  const set = (key, newVal) => onChange?.({ ...(value || {}), [key]: newVal });

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="structured-review">
      {schema.map((f) => (
        <div key={f.key} className={f.type === 'text' ? 'sm:col-span-2' : ''}>
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {f.label}
          </Label>
          {f.hint && (
            <p className="text-[10.5px] text-muted-foreground mb-1">{f.hint}</p>
          )}
          {f.type === 'text' ? (
            <Textarea
              value={value?.[f.key] || ''}
              onChange={(e) => set(f.key, e.target.value.slice(0, 600))}
              rows={2}
              disabled={disabled}
              data-testid={`field-${f.key}`}
            />
          ) : (
            <ChipInput
              values={Array.isArray(value?.[f.key]) ? value[f.key] : []}
              onChange={(v) => set(f.key, v)}
              disabled={disabled}
              testId={`field-${f.key}`}
            />
          )}
        </div>
      ))}
    </div>
  );
};

const ChipInput = ({ values, onChange, disabled, testId }) => {
  const [draft, setDraft] = React.useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(''); return; }
    onChange([...values, v].slice(0, 20));
    setDraft('');
  };
  const remove = (v) => onChange(values.filter((x) => x !== v));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex gap-1.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
          placeholder="Aggiungi voce e premi Invio…"
          maxLength={80}
          disabled={disabled}
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" variant="outline" onClick={add} disabled={disabled || !draft.trim()} data-testid={`${testId}-add`}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1">
        {values.length === 0 && (
          <span className="text-[11px] text-muted-foreground italic">nessuna voce</span>
        )}
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1 pl-2 pr-1">
            <span className="truncate max-w-[220px]">{v}</span>
            <button
              type="button"
              onClick={() => remove(v)}
              disabled={disabled}
              className="rounded-full hover:bg-background/60 p-0.5"
              aria-label={`Rimuovi ${v}`}
              data-testid={`${testId}-remove`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default StructuredReview;
